import { FileText, Loader2, Sparkles } from 'lucide-react';
import type { ActSourceRule } from '@hector/shared-types';
import { actSourceCardinalityLabel, actSourcePresentation } from '@hector/shared-types';

/**
 * Une pièce du dossier proposée pour une règle de source.
 *
 * Forme commune aux trois hooks de candidats : ils interrogent des périmètres
 * différents mais rendent la même chose.
 */
export interface ActSourceCandidate {
  id: string;
  name: string;
  /** Date intrinsèque de la pièce (colonne SQL `date`). Absente sur ~50–57 % des pièces. */
  document_date?: string | null;
}

interface ActSourceSelectorProps {
  rule: ActSourceRule;
  /** Le droit public renomme les onglets et parle de décision, pas de jugement. */
  publicLaw: boolean;
  candidates: ActSourceCandidate[] | undefined;
  selectedIds: readonly string[];
  /** Pièces qu'Hector a présélectionnées — cochées, et signalées comme telles. */
  suggestedIds: readonly string[];
  onChange: (documentIds: string[]) => void;
  isLoading?: boolean;
  error?: Error | null;
  disabled?: boolean;
  /** Motif de blocage propre À CETTE règle, jamais celui d'une voisine. */
  unmetReason?: string | null;
}

/**
 * Formate une date brute en date courte française (ex: "15/06/2024").
 *
 * WHY toLocaleDateString plutôt que slice(0,10) : node-postgres parse les colonnes
 * SQL `date` en Date à minuit LOCAL puis sérialise en UTC. Une pièce datée du
 * 2024-03-12 arrive donc comme "2024-03-11T23:00:00.000Z". Découper la chaîne ISO
 * afficherait "2024-03-11" (le 11, pas le 12). toLocaleDateString('fr-FR') lit la
 * valeur dans le fuseau du NAVIGATEUR et restitue la bonne date calendaire, quel
 * que soit le décalage DST.
 *
 * Retourne `null` si la valeur est absente ou non parsable, pour ne rien afficher
 * plutôt que "Invalid Date".
 */
function formatDocumentDate(raw: string): string | null {
  const d = new Date(raw);
  if (!Number.isFinite(d.getTime())) return null;
  return d.toLocaleDateString('fr-FR');
}

/**
 * Sélecteur de sources piloté par la règle du catalogue — un seul composant pour
 * les trois types de pièce.
 *
 * WHY un composant générique et pas un par type de source : un sélecteur dédié
 * par type oblige la carte à les brancher un à un, et fige son libellé français
 * dans son propre fichier. Un type déclaré au catalogue mais sans branchement
 * n'affiche alors rien du tout — le contrat partagé peut l'exiger, l'écran
 * l'ignore en silence, et le bouton Lancer reste grisé sans explication. Ici la
 * règle décide de tout : le titre, la cardinalité, la forme du contrôle (radio
 * si `maximum` vaut 1, sinon cases) et l'onglet à nommer quand le dossier est
 * vide. Les libellés viennent du contrat partagé, jamais d'ici.
 *
 * WHY l'état vide garde son en-tête : les anciens composants faisaient un
 * `return` anticipé qui remplaçait le bloc entier, légende comprise. L'avocat
 * qui n'avait aucune écriture adverse perdait du même coup l'information qu'il
 * lui en fallait une — il voyait un bouton « Lancer » grisé sans savoir
 * pourquoi. L'exigence reste donc affichée, et l'absence devient une consigne :
 * quel document déposer, et dans quel onglet.
 */
export function ActSourceSelector({
  rule,
  publicLaw,
  candidates,
  selectedIds,
  suggestedIds,
  onChange,
  isLoading = false,
  error = null,
  disabled = false,
  unmetReason = null,
}: ActSourceSelectorProps) {
  const { label, singularIndefini, location } = actSourcePresentation(rule.kind, { publicLaw });
  const choixUnique = rule.maximum === 1;
  const selected = new Set(selectedIds);
  const suggested = new Set(suggestedIds);
  const aDesSuggestions = suggestedIds.some((id) => selected.has(id));

  const basculer = (candidateId: string) => {
    if (choixUnique) {
      onChange([candidateId]);
      return;
    }
    onChange(selected.has(candidateId)
      ? selectedIds.filter((id) => id !== candidateId)
      : [...selectedIds, candidateId]);
  };

  // `fieldset` pour la cascade de `disabled` sur tous les contrôles ; le nom
  // accessible vient d'`aria-label` et non d'un `<legend>` en sr-only, qui
  // ferait entendre — et lire au test — deux fois le même intitulé.
  return (
    <fieldset
      className="rounded-lg border border-stone-200 bg-white overflow-hidden"
      aria-label={label}
      disabled={disabled}
    >
      <div className="flex items-baseline justify-between gap-3 px-3 py-2 border-b border-stone-100 bg-stone-50/70">
        <span className="text-xs font-medium text-stone-700">{label}</span>
        <span className="shrink-0 text-[10px] uppercase tracking-wide text-stone-400">
          {actSourceCardinalityLabel(rule)}
        </span>
      </div>

      {isLoading ? (
        <p className="flex items-center gap-2 px-3 py-2.5 text-xs text-stone-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Chargement…
        </p>
      ) : error ? (
        <p role="alert" className="px-3 py-2.5 text-xs text-red-700">
          Impossible de charger les pièces de {location}. Réessayez avant de lancer.
        </p>
      ) : !candidates || candidates.length === 0 ? (
        <p className="px-3 py-2.5 text-xs leading-relaxed text-amber-800 bg-amber-50/60">
          Aucune pièce disponible dans le dossier. Déposez {singularIndefini} dans {location},
          puis relancez. Elle servira aussi à vos actes suivants.
        </p>
      ) : (
        <div className="divide-y divide-stone-100">
          {candidates.map((candidate) => {
            const checked = selected.has(candidate.id);
            const plafondAtteint = rule.maximum !== null && selected.size >= rule.maximum;
            const formattedDate = candidate.document_date
              ? formatDocumentDate(candidate.document_date)
              : null;
            const hasBadge = formattedDate !== null || suggested.has(candidate.id);
            return (
              <label
                key={candidate.id}
                className={`flex cursor-pointer items-start gap-2.5 px-3 py-2 text-sm transition-colors ${
                  checked ? 'bg-stone-50 text-stone-900' : 'text-stone-700 hover:bg-stone-50/60'
                }`}
              >
                <input
                  type={choixUnique ? 'radio' : 'checkbox'}
                  name={choixUnique ? `act-source-${rule.kind}` : undefined}
                  checked={checked}
                  disabled={disabled || (!choixUnique && !checked && plafondAtteint)}
                  aria-label={candidate.name}
                  onChange={() => basculer(candidate.id)}
                  className={`mt-0.5 h-4 w-4 shrink-0 border-stone-300 ${choixUnique ? '' : 'rounded'}`}
                />
                <FileText className="mt-0.5 h-4 w-4 shrink-0 text-stone-400" />
                <span className="min-w-0 break-words">{candidate.name}</span>
                {hasBadge && (
                  <span className="ml-auto mt-0.5 shrink-0 flex items-center gap-2">
                    {formattedDate !== null && (
                      <span className="text-[10px] text-stone-400">{formattedDate}</span>
                    )}
                    {suggested.has(candidate.id) && (
                      <span
                        title="Présélectionné par Hector. Décochez si ce n'est pas la bonne pièce."
                        className="inline-flex shrink-0 items-center gap-1 text-[10px] text-stone-400"
                      >
                        <Sparkles className="h-3 w-3" />
                        suggéré
                      </span>
                    )}
                  </span>
                )}
              </label>
            );
          })}
        </div>
      )}

      {unmetReason && !isLoading && !error && candidates && candidates.length > 0 && (
        <p role="status" className="px-3 py-2 text-[11px] text-amber-800 bg-amber-50/60 border-t border-amber-100">
          {unmetReason}
        </p>
      )}
      {aDesSuggestions && !unmetReason && (
        <p className="px-3 py-1.5 text-[11px] text-stone-400 border-t border-stone-100">
          Hector a coché ce qu'il pense être la bonne pièce. Corrigez si besoin.
        </p>
      )}
    </fieldset>
  );
}
